import { useCallback, useEffect, useRef, useState } from 'react'
import {
  apiUrl,
  getAudioImageUrl,
  getPublicChannelAudioQueue,
  type PublicAudioTrack
} from '../../api'
import type { ChannelColorThemeId } from '../../channelColorThemes'

export type PublicAudioChannel = {
  name: string
  slug: string
  color_theme?: ChannelColorThemeId | undefined
  ready_audio_count: number
}

type AudioSlot = 'a' | 'b'

const preloadAheadCount = 2
const crossfadeSeconds = 6

const resolveMediaUrl = (url: string): string => {
  if (/^(https?:)?\/\//.test(url) || url.startsWith('data:')) {
    return url
  }

  return `${apiUrl}${url.startsWith('/') ? url : `/${url}`}`
}

const getRepeatWindowSize = (readyAudioCount: number): number =>
  Math.min(5, Math.max(0, readyAudioCount - 1))

const clampVolume = (value: number): number => Math.max(0, Math.min(1, value))

const isPageHidden = (): boolean =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden'

const supportsMediaSession = (): boolean =>
  typeof navigator !== 'undefined' && 'mediaSession' in navigator

export const useChannelAudioPlayer = () => {
  const audioARef = useRef<HTMLAudioElement | null>(null)
  const audioBRef = useRef<HTMLAudioElement | null>(null)
  const activeSlotRef = useRef<AudioSlot>('a')
  const channelRef = useRef<PublicAudioChannel | null>(null)
  const currentTrackRef = useRef<PublicAudioTrack | null>(null)
  const queueRef = useRef<PublicAudioTrack[]>([])
  const playedTrackHistoryRef = useRef<PublicAudioTrack[]>([])
  const recentTrackIdsRef = useRef<string[]>([])
  const standbyTrackIdRef = useRef<string | null>(null)
  const volumeRef = useRef(0.8)
  const isFadingRef = useRef(false)
  const queueRequestRef = useRef<Promise<void> | null>(null)
  const queueRequestGenerationRef = useRef(0)
  const queueRequestTokenRef = useRef<object | null>(null)
  const fadeFrameRef = useRef<number | null>(null)
  const generationRef = useRef(0)
  const advanceRef = useRef<(useCrossfade: boolean) => Promise<void>>(async () => {})

  const [channel, setChannel] = useState<PublicAudioChannel | null>(null)
  const [currentTrack, setCurrentTrack] = useState<PublicAudioTrack | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolumeState] = useState(0.8)

  const getAudio = useCallback((slot: AudioSlot): HTMLAudioElement | null =>
    slot === 'a' ? audioARef.current : audioBRef.current, [])

  const getActiveAudio = useCallback((): HTMLAudioElement | null =>
    getAudio(activeSlotRef.current), [getAudio])

  const getStandbyAudio = useCallback((): HTMLAudioElement | null =>
    getAudio(activeSlotRef.current === 'a' ? 'b' : 'a'), [getAudio])

  const setRecentTrackIds = useCallback((
    trackIds: string[],
    targetChannel: PublicAudioChannel | null
  ): void => {
    const windowSize = targetChannel ? getRepeatWindowSize(targetChannel.ready_audio_count) : 0

    recentTrackIdsRef.current = windowSize > 0 ? trackIds.slice(-windowSize) : []
  }, [])

  const stopAudio = useCallback((audio: HTMLAudioElement | null): void => {
    if (!audio) {
      return
    }

    audio.pause()
    audio.removeAttribute('src')
    audio.load()
    audio.volume = 0
  }, [])

  const cancelFade = useCallback((): void => {
    if (fadeFrameRef.current !== null) {
      window.cancelAnimationFrame(fadeFrameRef.current)
      fadeFrameRef.current = null
    }

    isFadingRef.current = false
  }, [])

  const updateDurationFromAudio = useCallback((audio: HTMLAudioElement): void => {
    const nextDuration = Number.isFinite(audio.duration) ? audio.duration : 0

    setDuration(nextDuration)
  }, [])

  const preloadStandby = useCallback((): void => {
    const nextTrack = queueRef.current[0]
    const standbyAudio = getStandbyAudio()

    if (!standbyAudio || !nextTrack) {
      standbyTrackIdRef.current = null
      stopAudio(standbyAudio)
      return
    }

    if (standbyTrackIdRef.current === nextTrack.id) {
      return
    }

    stopAudio(standbyAudio)
    standbyAudio.preload = 'auto'
    standbyAudio.src = resolveMediaUrl(nextTrack.file_url)
    standbyAudio.volume = 0
    standbyAudio.load()
    standbyTrackIdRef.current = nextTrack.id
  }, [getStandbyAudio, stopAudio])

  const refillQueue = useCallback(async (
    targetChannel = channelRef.current
  ): Promise<void> => {
    if (!targetChannel || queueRef.current.length >= preloadAheadCount) {
      preloadStandby()
      return
    }

    if (queueRequestRef.current && queueRequestGenerationRef.current === generationRef.current) {
      await queueRequestRef.current
      return
    }

    setIsLoadingNext(true)
    setErrorMessage(null)

    const requestGeneration = generationRef.current
    const requestToken = {}

    const request = (async (): Promise<void> => {
      try {
        const excludeTrackIds = Array.from(new Set([
          ...recentTrackIdsRef.current,
          ...(currentTrackRef.current ? [currentTrackRef.current.id] : []),
          ...queueRef.current.map((track) => track.id)
        ]))
        const tracks = await getPublicChannelAudioQueue(targetChannel.slug, {
          excludeTrackIds,
          currentTrackId: currentTrackRef.current?.id ?? null,
          limit: preloadAheadCount - queueRef.current.length
        })

        if (requestGeneration !== generationRef.current) {
          return
        }

        const existingTrackIds = new Set(queueRef.current.map((track) => track.id))
        const newTracks = tracks.filter((track) => !existingTrackIds.has(track.id))

        queueRef.current = [...queueRef.current, ...newTracks].slice(0, preloadAheadCount)
        preloadStandby()
      } catch (error) {
        if (queueRef.current.length === 0 && requestGeneration === generationRef.current) {
          setErrorMessage(error instanceof Error ? error.message : 'Unable to load upcoming tracks')
        }
      } finally {
        if (requestGeneration === generationRef.current) {
          setIsLoadingNext(false)
        }
        if (queueRequestTokenRef.current === requestToken) {
          queueRequestRef.current = null
          queueRequestTokenRef.current = null
        }
      }
    })()

    queueRequestRef.current = request
    queueRequestGenerationRef.current = requestGeneration
    queueRequestTokenRef.current = requestToken
    await request
  }, [preloadStandby])

  const promoteNextTrack = useCallback(async (
    nextTrack: PublicAudioTrack,
    options: { addToHistory: boolean, crossfade: boolean }
  ): Promise<void> => {
    const oldActiveSlot = activeSlotRef.current
    const nextSlot: AudioSlot = oldActiveSlot === 'a' ? 'b' : 'a'
    const oldAudio = getAudio(oldActiveSlot)
    const nextAudio = getAudio(nextSlot)
    const shouldReuseActiveAudio = isPageHidden() && oldAudio

    if (!nextAudio && !shouldReuseActiveAudio) {
      return
    }

    if (!shouldReuseActiveAudio && nextAudio && standbyTrackIdRef.current !== nextTrack.id) {
      stopAudio(nextAudio)
      nextAudio.preload = 'auto'
      nextAudio.src = resolveMediaUrl(nextTrack.file_url)
      nextAudio.load()
    }

    const currentTrack = currentTrackRef.current

    if (options.addToHistory && currentTrack) {
      playedTrackHistoryRef.current = [...playedTrackHistoryRef.current, currentTrack].slice(-20)
    }

    currentTrackRef.current = nextTrack
    setCurrentTrack(nextTrack)
    setCurrentTime(0)
    setDuration(nextTrack.duration_seconds ?? 0)
    setRecentTrackIds([...recentTrackIdsRef.current, nextTrack.id], channelRef.current)

    try {
      if (shouldReuseActiveAudio) {
        cancelFade()
        stopAudio(nextAudio)
        oldAudio.src = resolveMediaUrl(nextTrack.file_url)
        oldAudio.volume = volumeRef.current
        oldAudio.load()
        await oldAudio.play()
        setIsPlaying(true)
      } else if (options.crossfade && oldAudio && nextAudio) {
        nextAudio.volume = 0
        await nextAudio.play()
        activeSlotRef.current = nextSlot
        setIsPlaying(true)

        const fadeStartedAt = performance.now()
        const fadeDurationMs = crossfadeSeconds * 1000

        isFadingRef.current = true

        await new Promise<void>((resolve) => {
          const step = (now: number): void => {
            const progress = Math.min(1, (now - fadeStartedAt) / fadeDurationMs)
            const targetVolume = volumeRef.current

            oldAudio.volume = clampVolume(targetVolume * (1 - progress))
            nextAudio.volume = clampVolume(targetVolume * progress)
            setCurrentTime(nextAudio.currentTime)

            if (progress >= 1) {
              resolve()
              return
            }

            fadeFrameRef.current = window.requestAnimationFrame(step)
          }

          fadeFrameRef.current = window.requestAnimationFrame(step)
        })

        oldAudio.pause()
        oldAudio.removeAttribute('src')
        oldAudio.load()
        oldAudio.volume = 0
        nextAudio.volume = volumeRef.current
      } else {
        stopAudio(oldAudio)
        activeSlotRef.current = nextSlot
        nextAudio!.volume = volumeRef.current
        await nextAudio!.play()
        setIsPlaying(true)
      }

      standbyTrackIdRef.current = null
      isFadingRef.current = false
      void refillQueue()
    } catch (error) {
      isFadingRef.current = false
      setIsPlaying(false)
      setErrorMessage(error instanceof Error ? error.message : 'Unable to play channel audio')
    }
  }, [cancelFade, getAudio, refillQueue, setRecentTrackIds, stopAudio])

  const advanceToNext = useCallback(async (useCrossfade: boolean): Promise<void> => {
    if (!channelRef.current) {
      return
    }

    if (queueRef.current.length === 0) {
      await refillQueue()
    }

    const nextTrack = queueRef.current.shift()

    if (!nextTrack) {
      setIsPlaying(false)
      setErrorMessage('Unable to load upcoming tracks')
      return
    }

    await promoteNextTrack(nextTrack, { addToHistory: true, crossfade: useCrossfade })
  }, [promoteNextTrack, refillQueue])

  useEffect(() => {
    advanceRef.current = advanceToNext
  }, [advanceToNext])

  useEffect(() => {
    const audioA = new Audio()
    const audioB = new Audio()
    const audioElements = [audioA, audioB]

    audioElements.forEach((audio) => {
      audio.preload = 'auto'
      audio.volume = 0
    })

    audioARef.current = audioA
    audioBRef.current = audioB

    const handleEnded = (audio: HTMLAudioElement): void => {
      if (audio === getActiveAudio() && !isFadingRef.current) {
        void advanceRef.current(false)
      }
    }
    const handleTimeUpdate = (audio: HTMLAudioElement): void => {
      if (audio !== getActiveAudio() || isFadingRef.current) {
        return
      }

      setCurrentTime(audio.currentTime)

      const hasKnownDuration = Number.isFinite(audio.duration) && audio.duration > crossfadeSeconds
      const shouldCrossfade = hasKnownDuration
        && !isFadingRef.current
        && !isPageHidden()
        && queueRef.current.length > 0
        && audio.duration - audio.currentTime <= crossfadeSeconds

      if (shouldCrossfade) {
        void advanceRef.current(true)
      }
    }
    const handleLoadedMetadata = (audio: HTMLAudioElement): void => {
      if (audio === getActiveAudio()) {
        updateDurationFromAudio(audio)
      }
    }
    const handlePause = (audio: HTMLAudioElement): void => {
      if (audio === getActiveAudio() && !isFadingRef.current) {
        setIsPlaying(false)
      }
    }
    const handlePlay = (audio: HTMLAudioElement): void => {
      if (audio === getActiveAudio()) {
        setIsPlaying(true)
      }
    }

    audioElements.forEach((audio) => {
      const ended = (): void => handleEnded(audio)
      const timeupdate = (): void => handleTimeUpdate(audio)
      const loadedmetadata = (): void => handleLoadedMetadata(audio)
      const pause = (): void => handlePause(audio)
      const play = (): void => handlePlay(audio)

      audio.addEventListener('ended', ended)
      audio.addEventListener('timeupdate', timeupdate)
      audio.addEventListener('loadedmetadata', loadedmetadata)
      audio.addEventListener('pause', pause)
      audio.addEventListener('play', play)

      ;(audio as HTMLAudioElement & {
        mavolyraCleanup?: () => void
      }).mavolyraCleanup = () => {
        audio.removeEventListener('ended', ended)
        audio.removeEventListener('timeupdate', timeupdate)
        audio.removeEventListener('loadedmetadata', loadedmetadata)
        audio.removeEventListener('pause', pause)
        audio.removeEventListener('play', play)
      }
    })

    const handleVisibilityChange = (): void => {
      if (!isPageHidden() || !isFadingRef.current) {
        return
      }

      const activeAudio = getActiveAudio()
      const standbyAudio = getStandbyAudio()

      cancelFade()
      stopAudio(standbyAudio)

      if (activeAudio) {
        activeAudio.volume = volumeRef.current
        setIsPlaying(!activeAudio.paused)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      cancelFade()
      audioElements.forEach((audio) => {
        ;(audio as HTMLAudioElement & { mavolyraCleanup?: () => void }).mavolyraCleanup?.()
        stopAudio(audio)
      })
      audioARef.current = null
      audioBRef.current = null
    }
  }, [cancelFade, getActiveAudio, stopAudio, updateDurationFromAudio])

  const playInitialTrack = useCallback(async (
    nextChannel: PublicAudioChannel
  ): Promise<void> => {
    generationRef.current += 1
    cancelFade()
    stopAudio(audioARef.current)
    stopAudio(audioBRef.current)
    activeSlotRef.current = 'a'
    channelRef.current = nextChannel
    currentTrackRef.current = null
    queueRef.current = []
    standbyTrackIdRef.current = null
    recentTrackIdsRef.current = []
    playedTrackHistoryRef.current = []
    setChannel(nextChannel)
    setCurrentTrack(null)
    setCurrentTime(0)
    setDuration(0)
    setErrorMessage(null)

    await refillQueue(nextChannel)
    const firstTrack = queueRef.current.shift()

    if (!firstTrack) {
      setIsPlaying(false)
      setErrorMessage('No playable audio tracks found for this channel')
      return
    }

    const activeAudio = getActiveAudio()

    if (!activeAudio) {
      return
    }

    currentTrackRef.current = firstTrack
    setCurrentTrack(firstTrack)
    setDuration(firstTrack.duration_seconds ?? 0)
    setRecentTrackIds([firstTrack.id], nextChannel)
    activeAudio.src = resolveMediaUrl(firstTrack.file_url)
    activeAudio.volume = volumeRef.current

    try {
      await activeAudio.play()
      setIsPlaying(true)
      preloadStandby()
      void refillQueue(nextChannel)
    } catch (error) {
      setIsPlaying(false)
      setErrorMessage(error instanceof Error ? error.message : 'Unable to play channel audio')
    }
  }, [cancelFade, getActiveAudio, preloadStandby, refillQueue, setRecentTrackIds, stopAudio])

  const playChannel = useCallback(async (nextChannel: PublicAudioChannel): Promise<void> => {
    const isSameChannel = channelRef.current?.slug === nextChannel.slug

    if (isSameChannel && currentTrackRef.current) {
      const activeAudio = getActiveAudio()

      if (!activeAudio) {
        return
      }

      if (activeAudio.paused) {
        try {
          await activeAudio.play()
          setIsPlaying(true)
        } catch (error) {
          setErrorMessage(error instanceof Error ? error.message : 'Unable to resume channel audio')
        }
      } else {
        activeAudio.pause()
      }

      return
    }

    await playInitialTrack(nextChannel)
  }, [getActiveAudio, playInitialTrack])

  const pause = useCallback((): void => {
    const wasFading = isFadingRef.current
    const activeAudio = getActiveAudio()
    const standbyAudio = getStandbyAudio()

    if (wasFading) {
      cancelFade()
      stopAudio(standbyAudio)
    } else {
      standbyAudio?.pause()
    }

    if (activeAudio) {
      activeAudio.pause()
      activeAudio.volume = volumeRef.current
    }

    setIsPlaying(false)
  }, [cancelFade, getActiveAudio, getStandbyAudio, stopAudio])

  const resume = useCallback(async (): Promise<void> => {
    const activeAudio = getActiveAudio()

    if (!activeAudio || !currentTrackRef.current) {
      return
    }

    try {
      await activeAudio.play()
      setIsPlaying(true)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Unable to resume channel audio')
    }
  }, [getActiveAudio])

  const next = useCallback(async (): Promise<void> => {
    const standbyAudio = getStandbyAudio()

    cancelFade()
    stopAudio(standbyAudio)
    standbyTrackIdRef.current = null
    await advanceToNext(false)
  }, [advanceToNext, cancelFade, getStandbyAudio, stopAudio])

  const previous = useCallback(async (): Promise<void> => {
    const previousTrack = playedTrackHistoryRef.current.pop()

    if (!previousTrack) {
      return
    }

    const currentTrack = currentTrackRef.current
    const standbyAudio = getStandbyAudio()

    cancelFade()
    stopAudio(standbyAudio)
    standbyTrackIdRef.current = null

    if (currentTrack) {
      queueRef.current = [currentTrack, ...queueRef.current]
    }

    await promoteNextTrack(previousTrack, { addToHistory: false, crossfade: false })
  }, [cancelFade, getStandbyAudio, promoteNextTrack, stopAudio])

  const seek = useCallback((time: number): void => {
    const activeAudio = getActiveAudio()

    if (!activeAudio || !Number.isFinite(time)) {
      return
    }

    const nextTime = Math.max(0, Math.min(time, Number.isFinite(activeAudio.duration) ? activeAudio.duration : time))

    activeAudio.currentTime = nextTime
    setCurrentTime(nextTime)
  }, [getActiveAudio])

  const setVolume = useCallback((value: number): void => {
    const nextVolume = clampVolume(value)

    volumeRef.current = nextVolume
    setVolumeState(nextVolume)

    const activeAudio = getActiveAudio()

    if (activeAudio) {
      activeAudio.volume = isFadingRef.current ? activeAudio.volume : nextVolume
    }
  }, [getActiveAudio])

  const stop = useCallback((): void => {
    generationRef.current += 1
    cancelFade()
    stopAudio(audioARef.current)
    stopAudio(audioBRef.current)
    channelRef.current = null
    currentTrackRef.current = null
    queueRef.current = []
    recentTrackIdsRef.current = []
    playedTrackHistoryRef.current = []
    standbyTrackIdRef.current = null
    activeSlotRef.current = 'a'
    setChannel(null)
    setCurrentTrack(null)
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
    setErrorMessage(null)
    setIsLoadingNext(false)
  }, [cancelFade, stopAudio])

  useEffect(() => {
    if (!supportsMediaSession() || typeof MediaMetadata === 'undefined') {
      return
    }

    const mediaSession = navigator.mediaSession
    const artworkUrl = currentTrack ? getAudioImageUrl(currentTrack, 'preview') ?? getAudioImageUrl(currentTrack, 'thumb') : null

    mediaSession.metadata = currentTrack
      ? new MediaMetadata({
        album: channel?.name ?? 'Mavolyra',
        artist: currentTrack.artist ?? channel?.name ?? 'Mavolyra',
        artwork: artworkUrl ? [{ src: resolveMediaUrl(artworkUrl), sizes: '512x512' }] : [],
        title: currentTrack.title
      })
      : null

    return () => {
      mediaSession.metadata = null
    }
  }, [channel?.name, currentTrack])

  useEffect(() => {
    if (!supportsMediaSession()) {
      return
    }

    const mediaSession = navigator.mediaSession

    mediaSession.playbackState = isPlaying ? 'playing' : currentTrack ? 'paused' : 'none'
  }, [currentTrack, isPlaying])

  useEffect(() => {
    if (!supportsMediaSession()) {
      return
    }

    const mediaSession = navigator.mediaSession

    if (currentTrack && duration > 0) {
      try {
        mediaSession.setPositionState({
          duration,
          playbackRate: 1,
          position: Math.min(currentTime, duration)
        })
      } catch {
        // Some browsers expose Media Session without full position-state support.
      }
    }
  }, [currentTime, currentTrack, duration])

  useEffect(() => {
    if (!supportsMediaSession()) {
      return
    }

    const mediaSession = navigator.mediaSession

    mediaSession.setActionHandler('play', () => {
      void resume()
    })
    mediaSession.setActionHandler('pause', pause)
    mediaSession.setActionHandler('previoustrack', () => {
      void previous()
    })
    mediaSession.setActionHandler('nexttrack', () => {
      void next()
    })
    mediaSession.setActionHandler('stop', stop)

    return () => {
      mediaSession.setActionHandler('play', null)
      mediaSession.setActionHandler('pause', null)
      mediaSession.setActionHandler('previoustrack', null)
      mediaSession.setActionHandler('nexttrack', null)
      mediaSession.setActionHandler('stop', null)
      mediaSession.metadata = null
      mediaSession.playbackState = 'none'
    }
  }, [next, pause, previous, resume, stop])

  return {
    channel,
    currentTime,
    currentTrack,
    duration,
    errorMessage,
    isLoadingNext,
    isPlaying,
    next,
    pause,
    previous,
    playChannel,
    resume,
    seek,
    setVolume,
    stop,
    volume
  }
}
